import { Avatar, Box, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { InfoItem } from "./InfoItem";

export interface OrderInfoItem {
	icon: ReactNode;
	label: string;
	value?: string;
	show?: boolean;
	spacing?: number;
	isChip?: boolean;
	color?: string;
}

export interface OrderInfoCardProps {
	title: string;
	titleIcon?: ReactNode;
	mainValue?: string;
	items: OrderInfoItem[];
	minWidth?: number;
	maxWidth?: number;
}

export function OrderInfoCard({
	title,
	titleIcon,
	mainValue,
	items,
	minWidth = 200,
	maxWidth = 280,
}: OrderInfoCardProps) {
	// Filter items that should be shown
	const visibleItems = items.filter(
		(item) => item.show !== false && item.value,
	);
	if (visibleItems.length === 0) return null;
	return (
		<Box
			sx={{
				border: 1,
				borderColor: "divider",
				borderRadius: 2,
				p: 2,
				backgroundColor: "background.default",
				minWidth,
				maxWidth,
			}}
		>
			<Stack spacing={1.5}>
				{/* Card Title with Icon */}
				<Stack direction="row" alignItems="center" spacing={1}>
					{titleIcon && (
						<Avatar sx={{ width: 32, height: 32, bgcolor: "primary.main" }}>
							{titleIcon}
						</Avatar>
					)}
					<Box>
						<Typography
							variant={`${mainValue ? "body2" : "body1"}`}
							ml={mainValue ? 0 : titleIcon ? 5 : 0}
							color={mainValue ? "text.secondary" : "text.primary"}
							align="left"
						>
							{title}
						</Typography>
						{mainValue && <Typography variant="body1">{mainValue}</Typography>}
					</Box>
				</Stack>

				{/* Additional Information */}
				{visibleItems.length > 0 && (
					<Stack spacing={0.5}>
						{visibleItems.map((item, index) => (
							<InfoItem
								key={index}
								icon={item.icon}
								label={item.label}
								value={item.value!}
								spacing={item.spacing}
								isChip={item.isChip}
								color={item.color}
							/>
						))}
					</Stack>
				)}
			</Stack>
		</Box>
	);
}
